import { inject, Injectable } from "@angular/core";
import {
  EnrichmentService,
  InsuranceWritePayload,
} from "@app/shared/services/enrichment.service";
import { BlobPublicService } from "@app/shared/services/blob-public.service";
import {
  catchError,
  concatMap,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
} from "rxjs";
import {
  ENRICHMENT_HEADER_TRANSPORT,
  EnrichmentDataService,
} from "../../../services/enrichment-data.service";
import {
  FlatEnrichmentModel,
  detailToModel,
  isFlatEnrichmentModel,
  modelToDetail,
} from "@app/shared/mappers/enrichment-detail.adapter";
import {
  EnrichmentDetail,
  EnrichmentPriceItem,
  readTransitionedPriceIds,
} from "@app/shared/models/enrichment-detail.model";
import { isVoiceIvrChannel } from "@app/shared/constants/channel.constants";
import { validatePriceDiscount } from "../../../services/publish/validators/flat-detail.validator";
import { seedSellingWindowFromValidFor } from "../../../utils/selling-window.util";
import { EnrichmentPriceDirtyStore } from "./enrichment-price-dirty.store";
import { InsuranceMasterEditStore } from "./insurance-master-edit.store";
import { EnrichmentPriceLockStore } from "./enrichment-price-lock.store";
import { EnrichmentIsSofEditStore } from "./enrichment-is-sof-edit.store";
import { EnrichmentDisplayQuantityLockStore } from "./enrichment-display-quantity-lock.store";
import { buildPriceSaveBody } from "./price-save-body.util";
import { SectionDirtyStore } from "./section-dirty.store";
import { SavedScope } from "./snapshot-merge.util";
import { PermissionService } from "@app/core/services/permission.service";
import { mayAuthorCatalogOwnedFigures } from "../../../utils/price-authority.util";
import { PublishSchedule } from "@app/shared/models/publish-schedule.model";
import {
  DEFAULT_PRICE_SAVE_DECISION,
  PriceSaveDecision,
} from "./save-confirm.util";

/** Result emitted by `savePackageSection`/`savePrices` once the PATCH has
 *  succeeded. `saved` is what the server now holds for what was sent, so
 *  the shell can advance its discard snapshot by exactly that when the
 *  model itself is not a safe discard target. */
export interface SaveOk {
  ok: true;
  lifecycleStatus: string;
  saved: SavedScope;
}

export interface TransitionOk {
  ok: true;
  lifecycleStatus: string;
}

/** A save error the shell needs to render.
 *  - `"validation"` — a client-side pre-flight block (e.g. an invalid price
 *    discount). The PATCH was never sent; retrying the identical body would
 *    fail the same way, so the shell surfaces `raw`'s message and routes the
 *    user to fix the field instead of offering a blind Retry.
 *  - `"other"` — a genuine backend/transport failure where Retry is meaningful.
 *  - `"conflict"` — retained for shell/spec compatibility; the flat PATCH
 *    contract has no ETag/412 path so it is never emitted today. */
export interface SaveErr {
  ok: false;
  kind: "conflict" | "other" | "validation";
  raw: unknown;
}

/** The price fan-out (`savePrices`) landed SOME price PATCHes, but at least
 *  one price failed. Saved ids are already cleared from the dirty store
 *  and their responses committed to the model; failed ids stay dirty, so a
 *  Retry (= `savePrices` again) naturally re-sends only the failures. */
export interface SavePartial {
  ok: false;
  kind: "partial";
  failed: { priceId: string; label: string; raw: unknown }[];
  savedPrices: EnrichmentPriceItem[];
  /** The first failure's raw error — the representative cause, so generic
   *  `result.raw` readers (loggers, message extractors) work on every
   *  not-ok result without narrowing on `kind`. */
  raw: unknown;
}

export type SaveResult = SaveOk | SaveErr | SavePartial;

export type TransitionResult = TransitionOk | SaveErr;

/** An empty list would publish or reject every eligible price on the wire. */
export type NonEmptyPriceIds = readonly [string, ...string[]];

/**
 * Outcome of queuing a transition. Deliberately NOT a {@link SaveResult}:
 * nothing was saved and no lifecycle status moved, so the fields that matter
 * are the rows the backend created — one per price, all under one batchId.
 */
export type ScheduleResult =
  { ok: true; schedules: PublishSchedule[] } | { ok: false; raw: unknown };

/** Human label for a price in error messages: default-locale name, first
 *  available locale, else the id. */
function priceLabel(price: EnrichmentPriceItem): string {
  const name = price.name;
  if (typeof name === "string" && name) return name;
  if (name && typeof name === "object") {
    const map = name as Record<string, string>;
    const first =
      map["th-TH"] || map["en-US"] || Object.values(map).find(Boolean);
    if (first) return first;
  }
  return price.id ?? "";
}

/** Per-price PATCH outcome collected by the fan-out. */
type PriceSaveOutcome =
  | { id: string; ok: true; saved: EnrichmentPriceItem }
  | { id: string; ok: false; label: string; raw: unknown };

/**
 * Orchestrates the per-price save fan-out (POP-lifecycle contract):
 * one `PATCH /api/v1/enrichment/{id}` carrying ONLY the package-level fields
 * (never `prices`, never `lifecycleStatus` — both are BE/CPC-owned now),
 * plus one `PATCH /api/v1/enrichment/price/{id}` per DIRTY price. Untouched
 * passthrough data still round-trips via the `__detail` side field.
 */
@Injectable()
export class EnrichmentSaveCoordinator {
  private readonly insuranceMasterEdits = inject(InsuranceMasterEditStore);
  private readonly blobPublic = inject(BlobPublicService);
  private readonly data = inject(EnrichmentDataService);
  private readonly enrichmentService = inject(EnrichmentService);
  private readonly priceDirty = inject(EnrichmentPriceDirtyStore);
  private readonly priceLock = inject(EnrichmentPriceLockStore);
  private readonly isSofEdits = inject(EnrichmentIsSofEditStore);
  private readonly quantityLock = inject(EnrichmentDisplayQuantityLockStore);
  private readonly sectionDirty = inject(SectionDirtyStore);
  private readonly permissionService = inject(PermissionService);

  /** The model as it is NOW, as a detail. A save commits onto this, not
   *  onto the copy it captured at send: the user keeps editing while the
   *  PATCH is in flight, and those keystrokes are not the server's to
   *  overwrite. Null when nothing flat is loaded. */
  private currentDetail(): EnrichmentDetail | null {
    const model = this.data.getEnrichment();
    return model && isFlatEnrichmentModel(model)
      ? modelToDetail(model, model.__detail)
      : null;
  }

  /**
   * Run the package half of the save: a single `PATCH /enrichment/{id}`
   * carrying every package-level field EXCEPT `prices` and `lifecycleStatus`
   * (both BE/CPC-owned now). Never touches a price PATCH, even when prices
   * are locally dirty — that fan-out is {@link savePrices}' job, run
   * separately by the caller. The committed model keeps every local price
   * exactly as it is at completion; only the package-level fields move.
   */
  savePackageSection(
    packageId: string,
    onComplete: () => void = () => undefined,
  ): Observable<SaveResult> {
    return new Observable<SaveResult>((subscriber) => {
      const detail = this.currentDetail();
      if (!detail) {
        subscriber.next({
          ok: false,
          kind: "other",
          raw: new Error("No enrichment data loaded"),
        });
        subscriber.complete();
        onComplete();
        return;
      }

      // Strip the price array and the CPC-owned lifecycleStatus from the
      // package body: prices are never sent from here, and an absent
      // lifecycleStatus on a differential PATCH means "no change".
      const {
        prices: _prices,
        lifecycleStatus: _poStatus,
        ...packageBody
      } = detail;
      const tabEpochAtSend = this.sectionDirty.tabEditEpoch();

      const sub = this.enrichmentService
        .saveEnrichment(packageId, packageBody, {
          ...ENRICHMENT_HEADER_TRANSPORT,
        })
        .pipe(finalize(onComplete))
        .subscribe({
          next: (pkgRes) => {
            const saved = pkgRes?.data ?? detail;
            const current = this.currentDetail() ?? detail;
            // The server's package fields, unless a package tab emitted
            // while the PATCH was in flight: then the local fields hold
            // newer edits and only the BE-stamped status comes from the
            // response. Prices are never this half's to move.
            const { prices: _serverPrices, ...packageFields } = saved;
            const base =
              this.sectionDirty.tabEditEpoch() === tabEpochAtSend
                ? packageFields
                : {
                    ...current,
                    lifecycleStatus:
                      saved.lifecycleStatus ?? current.lifecycleStatus,
                  };
            this.data.setEnrichment(
              detailToModel({ ...base, prices: current.prices ?? [] }),
            );
            subscriber.next({
              ok: true,
              lifecycleStatus: saved.lifecycleStatus ?? "",
              saved: { kind: "package", detail: saved },
            });
            subscriber.complete();
          },
          error: (err: unknown) => {
            subscriber.next({ ok: false, kind: "other", raw: err });
            subscriber.complete();
          },
        });

      return () => sub.unsubscribe();
    });
  }

  /**
   * Run the price half of the save: one `PATCH /enrichment/price/{id}` per
   * id in `priceIds` (in parallel — one failure doesn't cancel siblings).
   * Never touches the package PATCH; `packageId` is kept only for log/error
   * context. The caller decides which ids to send — this method does NOT
   * consult {@link EnrichmentPriceDirtyStore} to choose targets, so it can be
   * driven by a single price row's Save action as easily as by "all dirty
   * prices". The model is committed back to the store ONCE, onto the model
   * as it is at completion: each successful price response by id, with a
   * failed price keeping the user's local edits (and its dirty mark) so
   * Retry re-sends exactly the failures, and a price edited while its PATCH
   * was in flight keeping the newer edits (and its dirty mark) too.
   */
  savePrices(
    packageId: string,
    priceIds: readonly string[],
    onComplete: () => void = () => undefined,
    decision: PriceSaveDecision = DEFAULT_PRICE_SAVE_DECISION,
  ): Observable<SaveResult> {
    return new Observable<SaveResult>((subscriber) => {
      const detail = this.currentDetail();
      if (!detail) {
        subscriber.next({
          ok: false,
          kind: "other",
          raw: new Error("No enrichment data loaded"),
        });
        subscriber.complete();
        onComplete();
        return;
      }

      const { prices: allPrices = [] } = detail;
      // Same two-part gate the Pricing tab renders behind (see
      // `EnrichmentPricing.mayEditPrice`): the publish permission AND the
      // Administrator role. Re-checked here rather than trusted from the
      // view, because nothing re-locks a price when the user switches role
      // mid-session — a role could unlock a price, switch to one without
      // `enrichment:publish`, and save amounts the form had already stopped
      // offering.
      const mayEditPrice = mayAuthorCatalogOwnedFigures(this.permissionService);

      // A Service quantity (a display's 20 GB) is a DIFFERENT authority over a
      // different thing, so it gets its own name even though it evaluates the
      // same today: the BE authorizes a display amount on `enrichment:publish`,
      // the very permission behind the money pair. Folding the two into one
      // constant is what would make the price gate lie about what it guards
      // the day either side moves.
      const mayEditServiceQuantity = mayAuthorCatalogOwnedFigures(
        this.permissionService,
      );

      const idSet = new Set(priceIds);
      const targetPrices = allPrices
        .map((price, index) => ({ price, index }))
        .filter(({ price }) => !!price.id && idSet.has(price.id));

      // Pre-flight only what will actually be sent, but keep the human index
      // from the FULL array so "price 3" matches the Pricing tab's ordering.
      const discountError = targetPrices
        .map(({ price, index }) => ({
          index,
          message: validatePriceDiscount(price.priceDiscount),
        }))
        .find((item) => item.message);
      if (discountError) {
        subscriber.next({
          ok: false,
          kind: "validation",
          raw: new Error(
            `Invalid price discount on price ${discountError.index + 1}: ${discountError.message}`,
          ),
        });
        subscriber.complete();
        onComplete();
        return;
      }

      // On a voice channel the Availability card SHOWS the price's ValidFor in
      // the Selling window pickers whenever the author has set no date of
      // their own. That default used to be display-only, so the author read a
      // complete window and saved an empty one. Fill the unset bounds here so
      // what was shown is what gets stored.
      //
      // The gate is the voice FAMILY predicate — the same one the card itself
      // renders behind — so the form and the save can never disagree about
      // whether the feature is on. Channel comes from the same store that
      // becomes the `x-channel` header downstream.
      const seedSellingWindow = isVoiceIvrChannel(this.data.getChannel());

      const epochAtSend = new Map(
        targetPrices.map(({ price }) => [
          price.id,
          this.priceDirty.editEpochOf(price.id),
        ]),
      );

      const priceCalls = targetPrices.map(({ price: stored }) => {
        // Seeding returns a NEW price and never mutates `stored`: `allPrices`
        // below is also the fallback for prices whose PATCH fails, and a
        // failed save must not leave the model showing dates the BE never
        // received.
        const price = seedSellingWindow
          ? seedSellingWindowFromValidFor(stored)
          : stored;

        const priceBody = buildPriceSaveBody(price, {
          amountsUnlocked: mayEditPrice && this.priceLock.isUnlocked(price.id),
          unlockedQuantities: mayEditServiceQuantity
            ? new Set(this.quantityLock.unlocked().get(price.id)?.keys())
            : new Set<string>(),
          sourceOfFundToggle: {
            edited: this.isSofEdits.isEdited(price.id),
            channel: this.data.getChannel(),
            productType: detail.productType,
          },
        });
        const savePrice$ = this.enrichmentService.saveEnrichmentPrice(
          price.id!,
          priceBody,
          {
            ...ENRICHMENT_HEADER_TRANSPORT,
            // Sent for the whole batch when the user chose Keep on sale — the
            // BE leaves a price that is not on sale alone. Omitted otherwise
            // so the request is byte-identical to the plain save.
            ...(decision.keepLifecycleStatus
              ? { keepLifecycleStatus: true }
              : {}),
          },
        );
        const masterDraft = this.insuranceMasterEdits.get(price.id!);
        const save$ = masterDraft
          ? (() => {
              const insurancePayload$: Observable<InsuranceWritePayload> =
                masterDraft.image
                  ? this.blobPublic
                      .uploadOnce(
                        masterDraft.image,
                        `enricher/insurance/${masterDraft.id}`,
                      )
                      .pipe(
                        map((upload): InsuranceWritePayload => {
                          const url = upload.data?.url;
                          if (!url) {
                            throw new Error("Image upload returned no URL.");
                          }
                          const fileName =
                            masterDraft.image!.name.substring(
                              0,
                              masterDraft.image!.name.lastIndexOf("."),
                            ) || masterDraft.image!.name;
                          const mimtype =
                            upload.data?.mime_type ??
                            masterDraft.image!.type.substring(
                              masterDraft.image!.type.lastIndexOf("/") + 1,
                            );
                          return {
                            ...masterDraft.payload,
                            attachment: [
                              ...(masterDraft.payload.attachment ?? []).filter(
                                (attachment) =>
                                  attachment.attachmentType !== "image",
                              ),
                              {
                                attachmentType: "image" as const,
                                name: { "th-TH": fileName, "en-US": fileName },
                                url: { "th-TH": url, "en-US": url },
                                mimtype: {
                                  "th-TH": mimtype.replace(/^image\//i, ""),
                                  "en-US": mimtype.replace(/^image\//i, ""),
                                },
                              },
                            ],
                          };
                        }),
                      )
                  : of<InsuranceWritePayload>({
                      ...masterDraft.payload,
                    });

              return insurancePayload$.pipe(
                concatMap((payload) =>
                  this.enrichmentService.updateInsurance(
                    masterDraft.id,
                    payload,
                  ),
                ),
                concatMap((response) => {
                  this.insuranceMasterEdits.recordSaved(
                    masterDraft.id,
                    response.data?.lastUpdate,
                  );
                  this.insuranceMasterEdits.clear(price.id!);
                  return savePrice$;
                }),
              );
            })()
          : savePrice$;
        return save$.pipe(
          map((res): PriceSaveOutcome => ({
            id: price.id!,
            ok: true,
            // `stored`, NOT the seeded copy. A seeded bound is inherited, and
            // `sellingWindowStartAuthored` reads a present date as "the
            // author set this" — committing the seed on a response that did
            // not echo it would flip the provenance note to "Set here" about
            // a date nobody chose. Leaving it inherited is both truthful and
            // idempotent: the next save re-seeds the identical value.
            saved: res?.data ?? stored,
          })),
          catchError((err: unknown) =>
            of<PriceSaveOutcome>({
              id: price.id!,
              ok: false,
              label: priceLabel(price),
              raw: err,
            }),
          ),
        );
      });

      const sub = (
        priceCalls.length ? forkJoin(priceCalls) : of([] as PriceSaveOutcome[])
      )
        .pipe(finalize(onComplete))
        .subscribe({
          next: (outcomes) => {
            const savedById = new Map(
              outcomes
                .filter(
                  (o): o is Extract<PriceSaveOutcome, { ok: true }> => o.ok,
                )
                .map((o) => [o.id, o.saved]),
            );
            const failed = outcomes.filter(
              (o): o is Extract<PriceSaveOutcome, { ok: false }> => !o.ok,
            );

            // A sent price whose epoch did not move is settled: the server's
            // copy is the truth. One edited while the PATCH was in flight
            // keeps the user's newer copy and takes only the status the BE
            // stamped; it stays dirty and unlocked for the next save.
            const settled = (id: string) =>
              this.priceDirty.editEpochOf(id) === epochAtSend.get(id);

            const current = this.currentDetail() ?? detail;
            const prices = (current.prices ?? []).map((local) => {
              const saved = savedById.get(local.id);
              if (!saved) return local;
              return settled(local.id)
                ? saved
                : {
                    ...local,
                    lifecycleStatus:
                      saved.lifecycleStatus ?? local.lifecycleStatus,
                  };
            });
            const listed = new Set(prices.map((p) => p.id));
            for (const [id, saved] of savedById) {
              if (!listed.has(id)) prices.push(saved);
            }
            this.data.setEnrichment(detailToModel({ ...current, prices }));

            for (const id of savedById.keys()) {
              if (!settled(id)) continue;
              this.priceDirty.clearDirty(id);
              // A saved price re-locks: the amounts the author unlocked to
              // change are now the stored ones, so the card goes back to
              // its safe state and a second change needs a second
              // deliberate unlock. A price whose PATCH failed, or that was
              // edited mid-flight, keeps its unlock, so the next save still
              // carries the amounts.
              this.priceLock.lock(id);
              // The Source of Fund the author chose is now the stored one.
              this.isSofEdits.clearEdited(id);
              // Same for every Service quantity on that price: what was
              // authored is now stored, so the fields go back to showing
              // the stored figure and tracking resumes from there.
              this.quantityLock.lockPrice(id);
            }

            const savedPrices = [...savedById.values()];
            if (failed.length) {
              subscriber.next({
                ok: false,
                kind: "partial",
                failed: failed.map((f) => ({
                  priceId: f.id,
                  label: f.label,
                  raw: f.raw,
                })),
                savedPrices,
                raw: failed[0].raw,
              });
            } else {
              subscriber.next({
                ok: true,
                lifecycleStatus: detail.lifecycleStatus ?? "",
                saved: { kind: "prices", prices: savedPrices },
              });
            }
            subscriber.complete();
          },
          error: (err: unknown) => {
            subscriber.next({ ok: false, kind: "other", raw: err });
            subscriber.complete();
          },
        });

      return () => sub.unsubscribe();
    });
  }

  /**
   * Run the Publish. Hits the dedicated `PATCH /api/v1/enrichment/publish`
   * endpoint with the drawer's explicit `priceIds` batch (all-or-nothing —
   * the backend advances exactly those prices `designed → active|launched`;
   * the PO's own status is CPC-owned and untouched). The UI is refreshed
   * WITHOUT a refetch: from the full response document when one comes back,
   * else by patching the transitioned prices' statuses in place.
   */
  performPublish(
    packageId: string,
    priceIds: NonEmptyPriceIds,
    onComplete: () => void = () => undefined,
  ): Observable<TransitionResult> {
    return new Observable<TransitionResult>((subscriber) => {
      const sub = this.enrichmentService
        .publishEnrichment(packageId, { ...ENRICHMENT_HEADER_TRANSPORT }, [
          ...priceIds,
        ])
        .pipe(finalize(onComplete))
        .subscribe({
          next: (res) => {
            const saved = res?.data;
            if (saved?.prices?.length) {
              this.data.setEnrichment(detailToModel(saved));
            } else {
              const transitioned = readTransitionedPriceIds(res);
              this.patchPriceStatuses(
                transitioned.length ? transitioned : priceIds,
                "active",
              );
            }
            subscriber.next({
              ok: true,
              lifecycleStatus: saved?.lifecycleStatus ?? "",
            });
            subscriber.complete();
          },
          error: (err: unknown) => {
            subscriber.next({ ok: false, kind: "other", raw: err });
            subscriber.complete();
          },
        });

      return () => sub.unsubscribe();
    });
  }

  /** Overwrite `lifecycleStatus` on the given prices in the loaded model —
   *  the no-refetch path after publish/reject. Touches ONLY the status field
   *  (both `__detail.prices` and nothing else), so a dirty form's edits stay
   *  intact. Prefers the per-price status from `doc` (the response document)
   *  when it carries one, else stamps `fallbackStatus`. */
  private patchPriceStatuses(
    ids: readonly string[],
    fallbackStatus: string,
    doc?: { prices?: EnrichmentPriceItem[] } | null,
  ): void {
    if (!ids.length) return;
    const model = this.data.getEnrichment();
    if (!model || !isFlatEnrichmentModel(model)) return;
    const wanted = new Set(ids);
    const docById = new Map(
      (doc?.prices ?? [])
        .filter((p) => !!p.id)
        .map((p) => [p.id!, p.lifecycleStatus]),
    );
    const prices = (model.__detail.prices ?? []).map((p) =>
      p.id && wanted.has(p.id)
        ? { ...p, lifecycleStatus: docById.get(p.id) ?? fallbackStatus }
        : p,
    );
    const next: FlatEnrichmentModel = {
      ...model,
      __detail: { ...model.__detail, prices },
    };
    this.data.setEnrichment(next);
  }

  /**
   * Run the Reject — the inverse of {@link performPublish}: the backend moves
   * the targeted prices off sale (`active|launched → rejected`); the PO's own
   * status is CPC-owned and untouched.
   *
   * A deliberate difference from `performPublish`: it never pushes the FULL
   * response document into the data service. Reject can be offered on a dirty
   * form, and the route's CanDeactivate guard can still cancel the navigation
   * that follows — rebuilding every tab FormGroup from the backend doc would
   * swap the user's edits out from under them on a page they chose to stay
   * on. The call is status-only, so patching just the transitioned prices'
   * `lifecycleStatus` keeps the model correct without touching any edit.
   */
  performReject(
    packageId: string,
    priceIds: NonEmptyPriceIds,
    onComplete: () => void = () => undefined,
  ): Observable<TransitionResult> {
    return new Observable<TransitionResult>((subscriber) => {
      const sub = this.enrichmentService
        .rejectEnrichment(packageId, { ...ENRICHMENT_HEADER_TRANSPORT }, [
          ...priceIds,
        ])
        .pipe(finalize(onComplete))
        .subscribe({
          next: (res) => {
            const transitioned = readTransitionedPriceIds(res);
            this.patchPriceStatuses(
              transitioned.length ? transitioned : priceIds,
              "rejected",
              res?.data,
            );
            subscriber.next({
              ok: true,
              lifecycleStatus: res?.data?.lifecycleStatus ?? "rejected",
            });
            subscriber.complete();
          },
          error: (err: unknown) => {
            subscriber.next({ ok: false, kind: "other", raw: err });
            subscriber.complete();
          },
        });

      return () => sub.unsubscribe();
    });
  }

  /**
   * Queue the transition instead of running it: `PATCH /enrichment/publish`
   * with a `scheduledAt` (direction `publish`), or `PATCH /enrichment/unpublish`
   * (direction `unpublish`). Nothing goes on or off sale here.
   *
   * Which is why this does NOT touch the model the way publish and reject do:
   * no price changed status, so patching one would show a lie until the next
   * read. The created rows come back instead, and the caller refreshes its
   * schedule list from them.
   */
  performSchedule(
    packageId: string,
    priceIds: NonEmptyPriceIds,
    scheduledAt: string,
    direction: "publish" | "unpublish",
    onComplete: () => void = () => undefined,
  ): Observable<ScheduleResult> {
    return new Observable<ScheduleResult>((subscriber) => {
      const call =
        direction === "unpublish"
          ? this.enrichmentService.scheduleUnpublish(
              packageId,
              scheduledAt,
              [...priceIds],
              { ...ENRICHMENT_HEADER_TRANSPORT },
            )
          : this.enrichmentService.schedulePublish(
              packageId,
              scheduledAt,
              [...priceIds],
              { ...ENRICHMENT_HEADER_TRANSPORT },
            );

      const sub = call.pipe(finalize(onComplete)).subscribe({
        next: (res) => {
          subscriber.next({
            ok: true,
            schedules: res?.data?.publishSchedules ?? [],
          });
          subscriber.complete();
        },
        error: (err: unknown) => {
          subscriber.next({ ok: false, raw: err });
          subscriber.complete();
        },
      });

      return () => sub.unsubscribe();
    });
  }
}
